export default class DashboardRobustService {
  constructor ($http) {
    this.$http = $http
    this.baseUrl = 'reports2'
  }

  /**
   * Get comprehensive dashboard data from the reports2/dashboard-data endpoint.
   * @param {Object} filters - { start_date, end_date, store_ids[] }
   * @returns {Promise} Dashboard data
   */
  async getDashboardData (filters = {}) {
    try {
      const params = {}

      if (filters.start_date) params.start_date = this.formatDateForAPI(filters.start_date)
      if (filters.end_date) params.end_date = this.formatDateForAPI(filters.end_date)

      // store_ids must be sent as an array (PHP reads store_ids[])
      if (filters.store_ids && Array.isArray(filters.store_ids) && filters.store_ids.length) {
        params['store_ids[]'] = filters.store_ids
      }

      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/dashboard-data`, { params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      throw error
    }
  }

  /**
   * Get sales trends for a specific granularity (hourly/daily/weekly/monthly)
   * @param {Object} filters - { start_date, end_date, granularity, store_ids }
   * @returns {Promise} Chart.js-shaped trends data
   */
  async getSalesTrends (filters = {}) {
    const params = {}
    if (filters.start_date) params.start_date = this.formatDateForAPI(filters.start_date)
    if (filters.end_date) params.end_date = this.formatDateForAPI(filters.end_date)
    if (filters.granularity) params.granularity = filters.granularity
    if (filters.store_ids && Array.isArray(filters.store_ids) && filters.store_ids.length) {
      params['store_ids[]'] = filters.store_ids
    }
    const response = await this.$http.get(`${this.baseUrl}/dashboard-trends`, { params })
    return response.data
  }

  /**
   * Get KPI metrics
   * @param {Object} filters - Filter parameters
   * @returns {Promise} KPI data
   */
  async getKPIMetrics (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/kpis`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching KPI metrics:', error)
      throw error
    }
  }

  /**
   * Get sales performance data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Sales performance data
   */
  async getSalesPerformance (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/sales-performance`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching sales performance:', error)
      throw error
    }
  }

  /**
   * Get top products
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Top products data
   */
  async getTopProducts (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/top-products`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching top products:', error)
      throw error
    }
  }

  /**
   * Get payment methods data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Payment methods data
   */
  async getPaymentMethods (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/payment-methods`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching payment methods:', error)
      throw error
    }
  }

  /**
   * Get top performers (employees)
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Top performers data
   */
  async getTopPerformers (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/top-performers`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching top performers:', error)
      throw error
    }
  }

  /**
   * Get inventory alerts
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Inventory alerts data
   */
  async getInventoryAlerts (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/inventory-alerts`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching inventory alerts:', error)
      throw error
    }
  }

  /**
   * Get recent activity
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Recent activity data
   */
  async getRecentActivity (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/recent-activity`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching recent activity:', error)
      throw error
    }
  }

  /**
   * Get sales comparison data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Sales comparison data
   */
  async getSalesComparison (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/sales-comparison`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching sales comparison:', error)
      throw error
    }
  }

  /**
   * Get quick stats
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Quick stats data
   */
  async getQuickStats (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/quick-stats`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching quick stats:', error)
      throw error
    }
  }

  /**
   * Get category distribution
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Category distribution data
   */
  async getCategoryDistribution (filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', this.formatDateForAPI(filters.start_date))
      if (filters.end_date) params.append('end_date', this.formatDateForAPI(filters.end_date))
      if (filters.department_id) params.append('department_id', filters.department_id)
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.baseUrl}/category-distribution`, { params: params })
          .then(data => {
            resolve(data.data)
          })
          .catch(reject)
      })
    } catch (error) {
      console.error('Error fetching category distribution:', error)
      throw error
    }
  }

  /**
   * Get stores (departments)
   * @returns {Promise} Stores data
   */
  async getStores () {
    try {
      const response = await this.$http.get('departments')
      return response.data
    } catch (error) {
      console.error('Error fetching stores:', error)
      throw error
    }
  }

  /**
   * Format date for API
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDateForAPI (date) {
    if (date instanceof Date) {
      return date.toISOString().split('T')[0]
    }
    if (typeof date === 'string') {
      return date
    }
    return new Date().toISOString().split('T')[0]
  }
}
